import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'

import { detectCurrentApp } from './app-detection'

interface AccessDeniedConfig {
  userRole: string
  currentApp: 'dashboard' | 'admin'
  targetApp: 'dashboard' | 'admin'
  reason?: 'access_denied' | 'incomplete_registration'
}

/**
 * Show access denied popup and handle redirect based on user role and current app
 */
export const showAccessDeniedPopup = (config: AccessDeniedConfig) => {
  const { userRole, currentApp, targetApp } = config

  // Determine redirect URL based on target app and environment
  const getRedirectUrl = () => {
    const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'

    if (targetApp === 'admin') {
      return isDevelopment ? 'http://localhost:5174/' : 'https://admin.cloud.qse-corp.com/'
    } else {
      return isDevelopment ? 'http://localhost:5173/' : 'https://cloud.qse-corp.com/'
    }
  }

  const redirectUrl = getRedirectUrl()

  // Show the access denied modal
  useActiveModelStore.setState({
    data: {
      userRole,
      currentApp,
      targetApp,
      redirectUrl,
      reason: config.reason || 'access_denied',
      modalHeader: {
        title: 'Access Denied',
        showCloseButton: false,
      },
      onBeforeClose: () => false, // Prevent modal from closing
    },
    width: 'max-w-[500px]',
    modelActive: ModelControlEnum.ACCESS_DENIED,
    toggleModel: true,
    scrollBehavior: 'inside',
    // Note: This modal is non-dismissible - users can only proceed via auto-redirect or "Redirect Now" button
  })
}

/**
 * Check if user has access to current app and handle access denied scenarios
 */
export const checkAppAccess = (userRole: string): boolean => {
  const { appType } = detectCurrentApp()
  const currentApp: 'dashboard' | 'admin' = appType === 'admin' ? 'admin' : 'dashboard'

  // Define access rules according to requirements
  const accessRules = {
    SUPER_ADMIN: {
      allowedApps: ['admin'],
      defaultApp: 'admin',
    },
    TENANT_ADMIN: {
      allowedApps: ['admin', 'dashboard'],
      defaultApp: 'dashboard',
    },
    TENANT_USER: {
      allowedApps: ['dashboard'],
      defaultApp: 'dashboard',
    },
    RESELLER_ADMIN: {
      allowedApps: ['admin'],
      defaultApp: 'admin',
    },
  }

  const userRule = accessRules[userRole as keyof typeof accessRules]

  if (!userRule) {
    console.error(`Unknown user role: ${userRole}`)

    return false
  }

  // Check if user has access to current app
  const hasAccess = userRule.allowedApps.includes(currentApp)

  if (!hasAccess) {
    // Show access denied popup
    showAccessDeniedPopup({
      userRole,
      currentApp,
      targetApp: userRule.defaultApp as AccessDeniedConfig['targetApp'],
    })

    return false
  }

  return true
}
